import {
  AfterViewInit,
  Directive,
  ElementRef,
  HostListener,
  OnDestroy,
  Renderer2,
} from "@angular/core"

/**
 * Keeps auth floating labels in the "raised" state when the browser autofills
 * a value (Chrome often fills without firing input / without clearing :placeholder-shown).
 */
@Directive({
  selector: "input.auth-input",
  standalone: true,
})
export class AuthFloatLabelDirective implements AfterViewInit, OnDestroy {
  private fieldEl: HTMLElement | null = null
  private rafId = 0
  private timers: number[] = []

  constructor(
    private readonly el: ElementRef<HTMLInputElement>,
    private readonly renderer: Renderer2,
  ) {}

  ngAfterViewInit(): void {
    this.fieldEl = this.el.nativeElement.closest(".auth-field")
    this.sync()
    // Autofill can arrive after first paint
    this.timers.push(window.setTimeout(() => this.sync(), 50))
    this.timers.push(window.setTimeout(() => this.sync(), 200))
    this.timers.push(window.setTimeout(() => this.sync(), 600))
    this.timers.push(window.setTimeout(() => this.sync(), 1200))
  }

  ngOnDestroy(): void {
    if (this.rafId) cancelAnimationFrame(this.rafId)
    this.timers.forEach((id) => clearTimeout(id))
  }

  @HostListener("input")
  @HostListener("change")
  @HostListener("blur")
  @HostListener("focus")
  onValueMaybeChanged(): void {
    this.sync()
  }

  @HostListener("animationstart", ["$event"])
  onAnimationStart(event: AnimationEvent): void {
    if (
      event.animationName === "auth-autofill-start" ||
      event.animationName === "onAutoFillStart"
    ) {
      this.sync(true)
    }
  }

  private sync(forceFilled = false): void {
    if (this.rafId) cancelAnimationFrame(this.rafId)
    this.rafId = requestAnimationFrame(() => {
      const input = this.el.nativeElement
      const filled =
        forceFilled ||
        (input.value != null && String(input.value).length > 0) ||
        this.matchesAutofill(input)
      if (!this.fieldEl) return
      if (filled) {
        this.renderer.addClass(this.fieldEl, "auth-field--filled")
      } else {
        this.renderer.removeClass(this.fieldEl, "auth-field--filled")
      }
    })
  }

  private matchesAutofill(input: HTMLInputElement): boolean {
    try {
      return input.matches(":-webkit-autofill") || input.matches(":autofill")
    } catch {
      try {
        return input.matches(":-webkit-autofill")
      } catch {
        return false
      }
    }
  }
}
